import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { styled, keyframes } from "@mui/material/styles";

// Мерцание звезд
const twinkle = keyframes`
  0% { opacity: 0.3; }
  50% { opacity: 1; }
  100% { opacity: 0.3; }
`;

// Функция создаёт строку с radial-gradient для звезд
function generateStars(count: number, size: number) {
  const stars: string[] = [];
  for (let i = 0; i < count; i++) {
    const x = Math.random() * 100; // %
    const y = Math.random() * 100; // %
    stars.push(
      `radial-gradient(${size}px ${size}px at ${x}% ${y}%, white, transparent)`,
    );
  }
  return stars.join(",");
}

// Светлый градиентный фон
export const StarryBackground = styled("div")(() => {
  return {
    position: "relative",
    height: "100vh",
    width: "100vw",
    background:
      "linear-gradient(110deg, rgba(59, 190, 219, 1) 20%, rgba(238, 234, 252, 1) 112%, rgba(227, 247, 250, 1) 100%)",
    overflow: "hidden",
  };
});

// Легкий эффект для светлого фона
export const NebulaEffect = styled(Box)(() => ({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  background: `
        radial-gradient(ellipse at 20% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
        radial-gradient(ellipse at 80% 20%, rgba(255, 255, 255, 0.05) 0%, transparent 50%),
        radial-gradient(ellipse at 40% 80%, rgba(255, 255, 255, 0.08) 0%, transparent 50%)
    `,
  zIndex: 2,
}));

// Контейнер для контента
export const ContentContainer = styled(Box)(() => ({
  position: "relative",
  zIndex: 10,
  height: "100vh",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
}));

// Заголовок с логотипом
export const HeaderContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
  padding: "16px 24px",
  borderBottom: "1px solid rgba(59, 130, 246, 0.3)",
}));

export const TitleContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flex: 1,
}));

// Кнопка назад в космическом стиле
export const BackButton = styled(Button)(() => ({
  color: "white",
  fontSize: "16px",
  fontWeight: 600,
  textTransform: "none",
  transition: "all 0.3s ease",
  "&:hover": {
    color: "rgba(6, 182, 212, 0.8)",
    textShadow: "0 0 10px rgba(6, 182, 212, 0.5)",
    backgroundColor: "transparent",
  },
}));

// Стилизованная область контента
export const ContentArea = styled(Box)(() => ({
  flex: 1,
  background: "rgba(30, 41, 59, 0.3)",
  backdropFilter: "blur(16px)",
  border: "1px solid rgba(59, 130, 246, 0.2)",
  borderRadius: "12px",
  margin: "0 24px",
  padding: "16px",
  overflow: "auto",

  // Кастомные стили для скроллбара
  "&::-webkit-scrollbar": {
    width: "12px",
    height: "12px",
  },
  "&::-webkit-scrollbar-track": {
    background: "rgba(15, 23, 42, 0.3)",
    borderRadius: "6px",
    border: "1px solid rgba(59, 130, 246, 0.2)",
  },
  "&::-webkit-scrollbar-thumb": {
    background:
      "linear-gradient(135deg, rgba(59, 130, 246, 0.6) 0%, rgba(6, 182, 212, 0.6) 100%)",
    borderRadius: "6px",
    border: "1px solid rgba(59, 130, 246, 0.3)",
    transition: "all 0.3s ease",
    "&:hover": {
      background:
        "linear-gradient(135deg, rgba(59, 130, 246, 0.8) 0%, rgba(6, 182, 212, 0.8) 100%)",
      borderColor: "rgba(6, 182, 212, 0.5)",
      boxShadow: "0 0 10px rgba(59, 130, 246, 0.4)",
    },
    "&:active": {
      background:
        "linear-gradient(135deg, rgba(59, 130, 246, 1) 0%, rgba(6, 182, 212, 1) 100%)",
      boxShadow: "0 0 15px rgba(59, 130, 246, 0.6)",
    },
  },
  "&::-webkit-scrollbar-corner": {
    background: "rgba(15, 23, 42, 0.3)",
  },

  // Firefox стили для скроллбара
  scrollbarWidth: "thin",
  scrollbarColor: "rgba(59, 130, 246, 0.6) rgba(15, 23, 42, 0.3)",
}));

// Стилизованные вкладки
export const StyledTabs = styled(Box)(() => ({
  "& .MuiTabs-root": {
    borderBottom: "1px solid rgba(59, 130, 246, 0.3)",
  },
  "& .MuiTab-root": {
    color: "rgba(147, 197, 253, 0.8)",
    fontWeight: 600,
    textTransform: "none",
    "&:hover": {
      color: "rgba(6, 182, 212, 0.8)",
    },
    "&.Mui-selected": {
      color: "white",
    },
  },
  "& .MuiTabs-indicator": {
    backgroundColor: "rgba(6, 182, 212, 0.8)",
  },
}));

// Стилизованный контейнер справки
export const StyledcertificateBox = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: 16,
  padding: "12px 16px",
  borderBottom: "1px solid rgba(59, 130, 246, 0.2)",
  justifyContent: "space-between",
  backgroundColor: "rgba(30, 41, 59, 0.3)",
  borderRadius: "8px",
  marginBottom: "8px",
  transition: "all 0.3s ease",
  border: "1px solid rgba(59, 130, 246, 0.2)",

  "&:hover": {
    cursor: "pointer",
    backgroundColor: "rgba(59, 130, 246, 0.1)",
    borderColor: "rgba(6, 182, 212, 0.5)",
    transform: "translateY(-2px)",
    boxShadow: "0 4px 15px rgba(59, 130, 246, 0.2)",
  },
}));

// Стилизованный текст
export const StyledTypography = styled(Typography)(() => ({
  color: "rgba(147, 197, 253, 0.8)",
  fontSize: "14px",
}));

// Стилизованная пагинация
export const StyledPagination = styled(Box)(() => ({
  "& .MuiPaginationItem-root": {
    color: "white",
    border: "1px solid rgba(59, 130, 246, 0.3)",
    "&:hover": {
      backgroundColor: "rgba(59, 130, 246, 0.2)",
      borderColor: "rgba(6, 182, 212, 0.5)",
    },
    "&.Mui-selected": {
      backgroundColor: "rgba(59, 130, 246, 0.4)",
      borderColor: "rgba(6, 182, 212, 0.8)",
    },
  },
}));

// Футер с элементами управления
export const FooterContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "16px 24px",
  borderTop: "1px solid rgba(59, 130, 246, 0.3)",
  backgroundColor: "rgba(30, 41, 59, 0.3)",
  backdropFilter: "blur(16px)",
}));

// Стилизованная карточка справки
export const CertificateCard = styled(Box)(() => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  border: "1px solid rgba(59, 130, 246, 0.3)",
  borderRadius: "12px",
  padding: "16px",
  gap: "16px",
  backgroundColor: "rgba(30, 41, 59, 0.4)",
  backdropFilter: "blur(16px)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  marginBottom: "12px",

  "&:hover": {
    backgroundColor: "rgba(30, 41, 59, 0.6)",
    borderColor: "rgba(6, 182, 212, 0.5)",
    transform: "translateY(-2px)",
    boxShadow: "0 8px 25px rgba(59, 130, 246, 0.2)",
  },

  "&.expired": {
    borderColor: "rgba(239, 68, 68, 0.6)",
    backgroundColor: "rgba(239, 68, 68, 0.1)",
    "&:hover": {
      borderColor: "rgba(239, 68, 68, 0.8)",
      backgroundColor: "rgba(239, 68, 68, 0.2)",
      boxShadow: "0 8px 25px rgba(239, 68, 68, 0.3)",
    },
  },
}));

// Контейнер для информации о справке
export const CertificateInfo = styled(Box)(() => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: "8px",
}));

// Контейнер для кнопок
export const CertificateActions = styled(Box)(() => ({
  display: "flex",
  gap: "12px",
  height: "40px",
  alignItems: "center",
}));

// Стилизованная кнопка отказа
export const DenyButton = styled(Button)(() => ({
  background:
    "linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(220, 38, 38, 0.2) 100%)",
  backdropFilter: "blur(16px)",
  border: "1px solid rgba(239, 68, 68, 0.3)",
  color: "white",
  textTransform: "none",
  fontSize: "14px",
  fontWeight: 600,
  borderRadius: "8px",
  minWidth: "100px",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",

  "&:hover": {
    background:
      "linear-gradient(135deg, rgba(239, 68, 68, 0.4) 0%, rgba(220, 38, 38, 0.4) 100%)",
    borderColor: "rgba(239, 68, 68, 0.8)",
    transform: "translateY(-2px)",
    boxShadow: "0 8px 25px rgba(239, 68, 68, 0.4)",
  },

  "&:disabled": {
    background: "rgba(239, 68, 68, 0.1)",
    color: "rgba(255, 255, 255, 0.5)",
    borderColor: "rgba(239, 68, 68, 0.1)",
  },
}));

// Стилизованная кнопка согласования
export const ApproveButton = styled(Button)(() => ({
  background:
    "linear-gradient(135deg, rgba(34, 197, 94, 0.2) 0%, rgba(22, 163, 74, 0.2) 100%)",
  backdropFilter: "blur(16px)",
  border: "1px solid rgba(34, 197, 94, 0.3)",
  color: "white",
  textTransform: "none",
  fontSize: "14px",
  fontWeight: 600,
  borderRadius: "8px",
  minWidth: "120px",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",

  "&:hover": {
    background:
      "linear-gradient(135deg, rgba(34, 197, 94, 0.4) 0%, rgba(22, 163, 74, 0.4) 100%)",
    borderColor: "rgba(34, 197, 94, 0.8)",
    transform: "translateY(-2px)",
    boxShadow: "0 8px 25px rgba(34, 197, 94, 0.4)",
  },

  "&:disabled": {
    background: "rgba(34, 197, 94, 0.1)",
    color: "rgba(255, 255, 255, 0.5)",
    borderColor: "rgba(34, 197, 94, 0.1)",
  },
}));

// Стилизованный текст в карточке
export const CertificateText = styled(Typography)(() => ({
  color: "rgba(147, 197, 253, 0.9)",
  fontSize: "14px",
  lineHeight: 1.5,

  "& strong": {
    color: "white",
    fontWeight: 600,
  },
}));

// Старый Wrapper для совместимости
export const Wrapper = styled(Box)(() => ({
  display: "flex",
  justifyContent: "center",
  flexDirection: "column",
  gap: 8,
  paddingLeft: 8,
  paddingRight: 8,
}));
